/**
 * 飞前检查记录：每次飞行前的清单结果与时间，可回看。
 *
 * 键里带检查时间（`check/{13 位毫秒}/{id}`），于是"最近的 N 次检查"直接取键最大的几条 ——
 * 不需要额外的索引存储，也不会因为写入顺序与检查顺序不一致而排错（多机同时检查时顺序会乱）。
 *
 * 记录**不会**因为"检查通过"而省略：一次通过的检查和一次被放弃的检查同样重要，
 * 因为复盘时真正要回答的是"起飞前到底检查了什么、结果如何"，而不是"有没有报错"。
 */

import { runSave, saveOk, type SaveOutcome, type StorageFailure } from './errors';
import {
  PREFLIGHT_PREFIX,
  RECORD_FORMAT,
  isReadableFormat,
  isRecordLike,
  preflightKey,
  readFiniteNumber,
  readString,
} from './keys';
import type { KeyValueStore, StoreOperation } from './store';

export type PreflightItemLevel = 'required' | 'recommended';
export type PreflightItemResult = 'pass' | 'fail' | 'skipped';
export type PreflightOutcome = 'pass' | 'fail' | 'aborted';

/** 一辆车的标识。审计日志与飞前检查都用它，两者才能对上 */
export interface VehicleTarget {
  readonly systemId: number;
  readonly componentId: number;
}

export interface PreflightCheckResult {
  /** 清单项的稳定 id（例如 `battery`、`gps-lock`），用于跨次对比"同一项这次过没过" */
  readonly id: string;
  /** 中文标签，直接显示 */
  readonly label: string;
  readonly level: PreflightItemLevel;
  readonly result: PreflightItemResult;
  readonly note?: string;
}

export interface PreflightRecordInput {
  readonly target: VehicleTarget;
  readonly outcome: PreflightOutcome;
  readonly items: readonly PreflightCheckResult[];
  readonly at?: number;
  readonly id?: string;
  readonly vehicleLabel?: string;
  readonly notes?: string;
  readonly durationMs?: number;
  readonly missionName?: string;
}

export interface PreflightRecord {
  readonly format: number;
  readonly id: string;
  readonly at: number;
  readonly target: VehicleTarget;
  readonly outcome: PreflightOutcome;
  readonly items: readonly PreflightCheckResult[];
  readonly vehicleLabel?: string;
  readonly notes?: string;
  readonly durationMs?: number;
  readonly missionName?: string;
}

function readTarget(value: unknown): VehicleTarget | null {
  if (!isRecordLike(value)) return null;
  const systemId = readFiniteNumber(value['systemId']);
  const componentId = readFiniteNumber(value['componentId']);
  if (systemId === null || componentId === null) return null;
  return { systemId, componentId };
}

function readLevel(value: unknown): PreflightItemLevel | null {
  return value === 'required' || value === 'recommended' ? value : null;
}

function readItemResult(value: unknown): PreflightItemResult | null {
  return value === 'pass' || value === 'fail' || value === 'skipped' ? value : null;
}

function readOutcome(value: unknown): PreflightOutcome | null {
  return value === 'pass' || value === 'fail' || value === 'aborted' ? value : null;
}

function readCheckResult(value: unknown): PreflightCheckResult | null {
  if (!isRecordLike(value)) return null;
  const id = readString(value['id']);
  const label = readString(value['label']);
  const level = readLevel(value['level']);
  const result = readItemResult(value['result']);
  if (id === null || label === null || level === null || result === null) return null;
  const note = readString(value['note']);
  return { id, label, level, result, ...(note === null ? {} : { note }) };
}

/** 读取校验：清单项坏掉的记录整条丢弃，而不是"尽量恢复"出一个缺项的检查结果 */
export function readPreflightRecord(value: unknown): PreflightRecord | null {
  if (!isRecordLike(value)) return null;
  if (!isReadableFormat(value['format'])) return null;
  const id = readString(value['id']);
  const at = readFiniteNumber(value['at']);
  const target = readTarget(value['target']);
  const outcome = readOutcome(value['outcome']);
  const rawItems = value['items'];
  if (id === null || at === null || target === null || outcome === null || !Array.isArray(rawItems)) {
    return null;
  }
  const items: PreflightCheckResult[] = [];
  for (const entry of rawItems) {
    const item = readCheckResult(entry);
    if (item === null) return null;
    items.push(item);
  }
  const vehicleLabel = readString(value['vehicleLabel']);
  const notes = readString(value['notes']);
  const durationMs = readFiniteNumber(value['durationMs']);
  const missionName = readString(value['missionName']);
  return {
    format: RECORD_FORMAT,
    id,
    at,
    target,
    outcome,
    items,
    ...(vehicleLabel === null ? {} : { vehicleLabel }),
    ...(notes === null ? {} : { notes }),
    ...(durationMs === null ? {} : { durationMs }),
    ...(missionName === null ? {} : { missionName }),
  };
}

/**
 * 清单结果汇总。
 *
 * 用"必需项有没有失败"而不是"总失败数"来判断能不能起飞：把推荐项失败也算成不能起飞，
 * 用户很快就会学会无视整个清单。这是飞前检查最经典的失效方式。
 */
export function summarizePreflight(items: readonly PreflightCheckResult[]): {
  readonly requiredFailed: number;
  readonly requiredSkipped: number;
  readonly passed: number;
  readonly advisoryFailed: number;
} {
  let requiredFailed = 0;
  let requiredSkipped = 0;
  let passed = 0;
  let advisoryFailed = 0;
  for (const item of items) {
    if (item.result === 'pass') passed += 1;
    if (item.result === 'skipped' && item.level === 'required') requiredSkipped += 1;
    if (item.result === 'fail') {
      if (item.level === 'required') requiredFailed += 1;
      else advisoryFailed += 1;
    }
  }
  return { requiredFailed, requiredSkipped, passed, advisoryFailed };
}

export interface PreflightRepositoryOptions {
  readonly now?: () => number;
  readonly onFailure?: (failure: StorageFailure) => void;
  /** 保留的检查记录份数上限，默认 200 */
  readonly maxRecords?: number;
  readonly newId?: () => string;
}

export interface SavePreflightOptions {
  /** 调用方上次读到的记录；这里只做冲突提示，不做阻塞（同一辆车不会有两人同时检查） */
  readonly expectedUpdatedAt?: number;
}

export class PreflightRepository {
  private readonly store: KeyValueStore;
  private readonly now: () => number;
  private readonly onFailure: ((failure: StorageFailure) => void) | undefined;
  private readonly maxRecords: number;
  private readonly newId: () => string;

  constructor(store: KeyValueStore, options: PreflightRepositoryOptions = {}) {
    this.store = store;
    this.now = options.now ?? Date.now;
    this.maxRecords = options.maxRecords ?? 200;
    this.newId = options.newId ?? defaultId;
  }

  async save(input: PreflightRecordInput): Promise<SaveOutcome<PreflightRecord>> {
    const outcome = await runSave(
      '保存飞前检查记录',
      async () => {
        const at = input.at ?? this.now();
        const id = input.id ?? this.newId();
        const record: PreflightRecord = {
          format: RECORD_FORMAT,
          id,
          at,
          target: input.target,
          outcome: input.outcome,
          items: input.items.map((item) => ({ ...item })),
          ...(input.vehicleLabel === undefined ? {} : { vehicleLabel: input.vehicleLabel }),
          ...(input.notes === undefined ? {} : { notes: input.notes }),
          ...(input.durationMs === undefined ? {} : { durationMs: input.durationMs }),
          ...(input.missionName === undefined ? {} : { missionName: input.missionName }),
        };

        const operations: StoreOperation[] = [
          { kind: 'put', collection: 'preflight-records', key: preflightKey(at, id), value: record },
        ];
        // 裁剪与写入同批，避免"记录写进去了但裁剪失败"导致无限增长
        const keys = await this.store.keys('preflight-records', PREFLIGHT_PREFIX);
        for (const stale of keys.slice(0, Math.max(0, keys.length - (this.maxRecords - 1)))) {
          operations.push({ kind: 'delete', collection: 'preflight-records', key: stale });
        }
        await this.store.batch(operations);
        return saveOk(record);
      },
      { collection: 'preflight-records' },
    );
    if (!outcome.ok) this.onFailure?.(outcome.failure);
    return outcome;
  }

  /** 按时间倒序列出检查记录；`limit` 默认 20（界面一次显示足够，回看更早的再翻页） */
  async list(options: { readonly limit?: number; readonly target?: VehicleTarget } = {}): Promise<readonly PreflightRecord[]> {
    const limit = options.limit ?? 20;
    const keys = await this.store.keys('preflight-records', PREFLIGHT_PREFIX);
    const records: PreflightRecord[] = [];
    // 键是定长时间戳前缀，字典序**倒序**就是时间倒序
    for (let index = keys.length - 1; index >= 0 && records.length < limit; index -= 1) {
      const key = keys[index];
      if (key === undefined) continue;
      const record = readPreflightRecord(await this.store.get('preflight-records', key));
      if (record === null) continue;
      if (options.target !== undefined && !sameTarget(record.target, options.target)) continue;
      records.push(record);
    }
    return records;
  }

  async latest(target?: VehicleTarget): Promise<PreflightRecord | null> {
    const records = await this.list(target === undefined ? { limit: 1 } : { limit: 1, target });
    return records[0] ?? null;
  }

  async count(): Promise<number> {
    return (await this.store.keys('preflight-records', PREFLIGHT_PREFIX)).length;
  }

  /** 主动清理：保留最近 `keep` 条，返回删掉的条数 */
  async prune(keep = this.maxRecords): Promise<SaveOutcome<number>> {
    const outcome = await runSave(
      '清理飞前检查记录',
      async () => {
        const keys = await this.store.keys('preflight-records', PREFLIGHT_PREFIX);
        const stale = keys.slice(0, Math.max(0, keys.length - keep));
        if (stale.length === 0) return saveOk(0);
        await this.store.batch(
          stale.map((key) => ({ kind: 'delete', collection: 'preflight-records', key }) as const),
        );
        return saveOk(stale.length);
      },
      { collection: 'preflight-records' },
    );
    if (!outcome.ok) this.onFailure?.(outcome.failure);
    return outcome;
  }
}

function sameTarget(left: VehicleTarget, right: VehicleTarget): boolean {
  return left.systemId === right.systemId && left.componentId === right.componentId;
}

function defaultId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
